import markets from "./markets";
import CustomSelect from "../components/select/dataGridList";
import CustomDateTimePicker from "../components/dateTimePickers/DateTimePicker";
import { DataFetcher } from "../utils/dataFetcher";
import moment from "moment-timezone";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import { getJSON } from "../utils/safeLocalStorage";
import { getRowCurrency } from "../utils/currencyUtils";
import { patternOptions } from "./patternOptions";
import {
  getServerTimePickerValue,
  convertServerTimePickerDate,
} from "../utils/timezones";
import Typography from "@mui/material/Typography";

const ColumnsEdit = (
  theme,
  throwMessage,
  idTradeGenerator,
  runDurationCal,
  handleOpenJournal,
  auth,
  calculateFields,
  selectedAccountId = null,
) => {
  const checkStopLossType = (p) => {
    const pairObject = markets.find((m) => m.ticker === p);
    if (pairObject?.multiplier === 10) {
      return "Ticks";
    } else if (pairObject?.multiplier === 1) {
      return "Pips";
    }
  };

  const handleEditCell = async (cell) => {
    const runFunction = async () => {
      const result = await DataFetcher("edit-trade", cell);
      if (result) {
        // Store current arrays before updating
        const currentTrades = [...auth.trades];
        const currentAllTrades = [...auth.allTrades];

        // Filter out the old trade and add the new one
        const filteredArray = currentTrades.filter((x) => x._id !== result._id);
        filteredArray.push(result);
        filteredArray.sort((a, b) => {
          return moment(a.created).diff(b.created);
        });
        auth.setTrades(filteredArray);

        // Also update allTrades
        const filteredAllArray = currentAllTrades.filter(
          (x) => x._id !== result._id,
        );
        filteredAllArray.push(result);
        filteredAllArray.sort((a, b) => {
          return moment(a.created).diff(b.created);
        });
        auth.setAllTrades(filteredAllArray);

        if (!auth.backtestingMode) {
          calculateFields(filteredArray, false);
        } else {
          calculateFields(filteredArray, true, result.testId);
        }
      } else {
        throwMessage("error", "Error Try Again");
      }
    };

    if (cell.id) {
      runFunction();
    } else {
      throwMessage("error", "Error Try Again");
    }
  };

  const runUpdate = (e, params, date) => {
    const id = params.row.id;
    const field = params.field;
    if (date) {
      const payload = { value: date, id: id, field: field };
      handleEditCell(payload);
    } else {
      const value = e.target.value;
      const payload = { value: value, id: id, field: field };
      handleEditCell(payload);
    }
  };

  const handleEntryServerTimeChange = (date, params) => {
    if (!date) return;
    const id = params.row.id;
    const account = getAccountForTrade(params.row);
    const originalTimezone = params.row.timezone;
    const converted = convertServerTimePickerDate(
      date,
      originalTimezone,
      account,
    );
    handleEditCell({ value: converted.date, id, field: "entryDate" });
    handleEditCell({ value: converted.time, id, field: "entryTime" });
  };

  const handleExitServerTimeChange = (date, params) => {
    if (!date) return;
    const id = params.row.id;
    const account = getAccountForTrade(params.row);
    const originalTimezone = params.row.timezone;
    const converted = convertServerTimePickerDate(
      date,
      originalTimezone,
      account,
    );
    handleEditCell({ value: converted.date, id, field: "exitDate" });
    handleEditCell({ value: converted.time, id, field: "exitTime" });
  };

  const user = getJSON("user", {});
  const capitalAccounts = user.capitalAccounts || [];
  const getAccountForTrade = (row) =>
    capitalAccounts.find((acc) => acc.id === row.accountId);

  let array = [
    {
      field: "pair",
      headerName: "Instrument",
      width: 120,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        if (!params.row.pair) {
          return "";
        }
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <p
              style={{
                cursor: "pointer",
                color: params.row.journalComplete ? (theme[0] === "dark" ? "#FCFCFF" : "black") : "#f23645",
                fontWeight: "400",
                margin: 0,
              }}
              onClick={() => {
                if (!params.row.journalID) {
                  throwMessage("error", "No Journal Found");
                  handleOpenJournal(params.row, true);
                } else {
                  handleOpenJournal(params.row, true);
                }
              }}
            >
              {params.row.pair}
            </p>
            {params.row.foreCastId ? (
              <span
                style={{
                  cursor: "pointer",
                  backgroundColor: "#26a65d",
                  color: "white",
                  fontSize: "10px",
                  padding: "1px 4px",
                  borderRadius: "3px",
                  fontWeight: "600",
                }}
                onClick={() => {
                  localStorage.setItem(
                    "forecast-to-view",
                    params.row.foreCastId,
                  );
                  localStorage.setItem("screen", 0);
                  auth.setTab(0);
                  localStorage.setItem("from-tracker", true);
                  // Save the current filter state before opening forecast
                  localStorage.setItem("tracker-trade-filter-before-forecast", localStorage.getItem("trade-to-view") || "");
                  localStorage.setItem("tracker-linked-filter-before-forecast", localStorage.getItem("linked-trade-ids") || "");
                }}
              >
                F
              </span>
            ) : (
              <span
                style={{
                  cursor: "pointer",
                  backgroundColor: "#f23645",
                  color: "white",
                  fontSize: "10px",
                  padding: "1px 4px",
                  borderRadius: "3px",
                  fontWeight: "600",
                }}
                onClick={() => {
                  throwMessage("info", "No Forecast Attached");
                }}
              >
                F
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: "direction",
      headerName: "Direction",
      width: 110,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        return (
          <CustomSelect
            color={
              params.row.direction === "Short"
                ? "#db0671"
                : params.row.direction === "Long"
                  ? "#0079cf"
                  : ""
            }
            size="small"
            placeholder="Direction"
            value={params.row.direction}
            onChange={(e) => runUpdate(e, params)}
            menuItemArray={JSON.stringify([
              { value: "Short", label: "Short" },
              { value: "Long", label: "Long" },
            ])}
          />
        );
      },
    },

    {
      field: "tradingStyle",
      headerName: "Style",
      width: 120,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        return (
          <CustomSelect
            key={`${params.row.id}-${params.row.tradingStyle || "Intraday"}`}
            size="small"
            value={params.row.tradingStyle || "Intraday"}
            onChange={(e) => {
              runUpdate(e, params);
            }}
            menuItemArray={JSON.stringify([
              { value: "Intraday", label: "Intraday" },
              { value: "Swing", label: "Swing" },
            ])}
          />
        );
      },
    },

    {
      field: "openPricePC",
      headerName: "Open Price",
      width: 110,
      editable: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (
          params.row.equityType === "Personal Capital" ||
          params.row.equityType === "Backtest"
        ) {
          if (!params.row.openPricePC) {
            return "";
          } else {
            return <p>{params.row.openPricePC}</p>;
          }
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "openPriceIC",
      headerName: "Open Price",
      width: 110,
      editable: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (params.row.equityType === "Investor Capital") {
          if (!params.row.openPriceIC) {
            return "";
          } else {
            return <p>{params.row.openPriceIC}</p>;
          }
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "closePricePC",
      headerName: "Close Price",
      width: 110,
      editable: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (
          params.row.equityType === "Personal Capital" ||
          params.row.equityType === "Backtest"
        ) {
          if (!params.row.closePricePC) {
            return "";
          } else {
            return <p>{params.row.closePricePC}</p>;
          }
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "closePriceIC",
      headerName: "Close Price",
      width: 110,
      editable: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (params.row.equityType === "Investor Capital") {
          if (!params.row.closePriceIC) {
            return "";
          } else {
            return <p>{params.row.closePriceIC}</p>;
          }
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "stopLossPricePC",
      headerName: "SL Price",
      width: 110,
      editable: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (
          params.row.equityType === "Personal Capital" ||
          params.row.equityType === "Backtest"
        ) {
          if (!params.row.stopLossPricePC) {
            return (
              <HorizontalRuleIcon
                sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
              />
            );
          } else {
            return <p>{params.row.stopLossPricePC}</p>;
          }
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "stopLossPriceIC",
      headerName: "SL Price",
      width: 110,
      editable: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (params.row.equityType === "Investor Capital") {
          if (!params.row.stopLossPriceIC) {
            return (
              <HorizontalRuleIcon
                sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
              />
            );
          } else {
            return <p>{params.row.stopLossPriceIC}</p>;
          }
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "stopLossPC",
      headerName: "Stop Loss",
      width: 110,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        if (
          params.row.equityType === "Personal Capital" ||
          params.row.equityType === "Backtest"
        ) {
          const distance = params.row.stopLossPC;

          if (distance === "" || distance === null || distance === undefined) {
            return "";
          }

          const unit = checkStopLossType(params.row.pair) || "";

          return (
            <p>
              {distance} {unit}
            </p>
          );
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "stopLossIC",
      headerName: "Stop Loss",
      width: 110,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        if (params.row.equityType === "Investor Capital") {
          const distance = params.row.stopLossIC;

          if (distance === "" || distance === null || distance === undefined) {
            return "";
          }

          const unit = checkStopLossType(params.row.pair) || "";

          return (
            <p>
              {distance} {unit}
            </p>
          );
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "profitLossDollarPersonal",
      headerName: "P/L Gross",
      width: 120,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        if (
          params.row.equityType === "Personal Capital" ||
          params.row.equityType === "Backtest"
        ) {
          const profitLoss = params.row.profitLossDollarPersonal;
          const currency = getRowCurrency(params.row, selectedAccountId, user);

          if (!profitLoss) {
            return <p style={{ color: "#ED9E0E" }}>{currency} 0</p>;
          }
          return (
            <p
              style={{
                color:
                  profitLoss < 0
                    ? "#f23645"
                    : profitLoss > 0
                      ? "#26a65d"
                      : "#ED9E0E",
              }}
            >
              {currency} {Math.abs(profitLoss).toFixed(2)}
            </p>
          );
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "profitLossDollarInvestor",
      headerName: "P/L Gross",
      width: 120,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        if (params.row.equityType === "Investor Capital") {
          const profitLoss = params.row.profitLossDollarInvestor;
          const currency = getRowCurrency(params.row, selectedAccountId, user);

          if (!profitLoss) {
            return <p style={{ color: "#ED9E0E" }}>{currency} 0</p>;
          }
          return (
            <p
              style={{
                color:
                  profitLoss < 0
                    ? "#f23645"
                    : profitLoss > 0
                      ? "#26a65d"
                      : "#ED9E0E",
              }}
            >
              {currency} {Math.abs(profitLoss).toFixed(2)}
            </p>
          );
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "feePC",
      headerName: "Fee",
      width: 100,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        if (
          params.row.equityType === "Personal Capital" ||
          params.row.equityType === "Backtest"
        ) {
          const fee = params.row.feePC;
          const currency = getRowCurrency(params.row, selectedAccountId, user);

          if (!fee) {
            return <p style={{ color: "#ED9E0E" }}>{currency} 0</p>;
          }
          return (
            <p
              style={{
                color: fee < 0 ? "#f23645" : fee > 0 ? "#26a65d" : "#ED9E0E",
              }}
            >
              {currency} {Math.abs(fee).toFixed(2)}
            </p>
          );
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "swapPC",
      headerName: "Swap",
      width: 100,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        if (
          params.row.equityType === "Personal Capital" ||
          params.row.equityType === "Backtest"
        ) {
          const swap = params.row.swapPC;
          const currency = getRowCurrency(params.row, selectedAccountId, user);

          if (!swap) {
            return <p style={{ color: "#ED9E0E" }}>{currency} 0</p>;
          }
          return (
            <p
              style={{
                color: swap < 0 ? "#f23645" : swap > 0 ? "#26a65d" : "#ED9E0E",
              }}
            >
              {currency} {Math.abs(swap).toFixed(2)}
            </p>
          );
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "feeIC",
      headerName: "Fee",
      width: 100,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        if (params.row.equityType === "Investor Capital") {
          const fee = params.row.feeIC;
          const currency = getRowCurrency(params.row, selectedAccountId, user);

          if (!fee) {
            return <p style={{ color: "#ED9E0E" }}>{currency} 0</p>;
          }
          return (
            <p
              style={{
                color: fee < 0 ? "#f23645" : fee > 0 ? "#26a65d" : "#ED9E0E",
              }}
            >
              {currency} {Math.abs(fee).toFixed(2)}
            </p>
          );
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "swapIC",
      headerName: "Swap",
      width: 100,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        if (params.row.equityType === "Investor Capital") {
          const swap = params.row.swapIC;
          const currency = getRowCurrency(params.row, selectedAccountId, user);

          if (!swap) {
            return <p style={{ color: "#ED9E0E" }}>{currency} 0</p>;
          }
          return (
            <p
              style={{
                color: swap < 0 ? "#f23645" : swap > 0 ? "#26a65d" : "#ED9E0E",
              }}
            >
              {currency} {Math.abs(swap).toFixed(2)}
            </p>
          );
        } else {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
      },
    },
    {
      field: "entryServerTime",
      headerName: "Entry (Server)",
      width: 220,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const account = getAccountForTrade(params.row);
        if (!params.row.entryDate || !account) {
          return <Typography sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }}>-</Typography>;
        }
        const value = getServerTimePickerValue(
          params.row.entryDate,
          params.row.entryTime,
          params.row.timezone,
          account,
        );
        return (
          <CustomDateTimePicker
            size="small"
            label=""
            value={value}
            onChange={(e) => handleEntryServerTimeChange(e.$d, params)}
            border="none"
            hideIcon
          />
        );
      },
    },
      {
      field: "exitServerTime",
      headerName: "Exit (Server)",
      width: 220,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const account = getAccountForTrade(params.row);
        if (!params.row.exitDate || !account) {
          return <Typography sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }}>-</Typography>;
        }
        const value = getServerTimePickerValue(
          params.row.exitDate,
          params.row.exitTime,
          params.row.timezone,
          account,
        );
        return (
          <CustomDateTimePicker
            size="small"
            label=""
            value={value}
            onChange={(e) => handleExitServerTimeChange(e.$d, params)}
            border="none"
            hideIcon
          />
        );
      },
    },

    {
      field: "highlightedPair",
      headerName: "Watchlist",
      width: 150,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        return (
          <CustomSelect
            size="small"
            placeholder="Highlighted Pair"
            value={params.row.highlightedPair}
            onChange={(e) => runUpdate(e, params)}
            menuItemArray={JSON.stringify([
              { value: "Main", label: "Main" },
              { value: "Additional", label: "Additional" },
              { value: "Potential", label: "Potential" },
              { value: "None", label: "None" },
            ])}
          />
        );
      },
    },
    {
      field: "grade",
      headerName: "Grade",
      width: 150,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        return (
          <CustomSelect
            color={
              params.row.grade === "Low Risk"
                ? "#26a65d"
                : params.row.grade === "High Risk"
                  ? "#FFA500"
                  : params.row.grade === "Invalid"
                    ? "#f23645"
                    : ""
            }
            size="small"
            placeholder="Grade"
            value={params.row.grade}
            onChange={(e) => runUpdate(e, params)}
            menuItemArray={JSON.stringify([
              { value: "Low Risk", label: "Low Risk" },
              { value: "High Risk", label: "High Risk" },
              { value: "Invalid", label: "Invalid" },
            ])}
          />
        );
      },
    },
    {
      field: "entryType",
      headerName: "Entry Type",
      width: 180,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        return (
          <CustomSelect
            size="small"
            placeholder="Entry Type"
            value={params.row.entryType}
            onChange={(e) => runUpdate(e, params)}
            menuItemArray={JSON.stringify([
              { value: "Risk", label: "Risk" },
              { value: "Reduced Risk", label: "Reduced Risk" },
            ])}
          />
        );
      },
    },
    {
      field: "entryTimeframe",
      headerName: "Entry TF",
      width: 120,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        return (
          <CustomSelect
            size="small"
            placeholder="Timeframe"
            value={params.row.entryTimeframe}
            onChange={(e) => runUpdate(e, params)}
            menuItemArray={JSON.stringify([
              { label: "5m", value: "5m" },
              { label: "15m", value: "15m" },
              { label: "1H", value: "1H" },
              { label: "4H", value: "4H" },
              { label: "D", value: "D" },
              { label: "W", value: "W" },
              { label: "M", value: "M" },
            ])}
          />
        );
      },
    },

    {
      field: "firstEntryOrScaleIn",
      headerName: "Entry No",
      width: 150,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        return (
          <CustomSelect
            size="small"
            placeholder="First Entry/Scale In"
            value={params.row.firstEntryOrScaleIn}
            onChange={(e) => runUpdate(e, params)}
            menuItemArray={JSON.stringify([
              { value: "1st Entry", label: "First Entry" },
              { value: "Scale In", label: "Scale In" },
            ])}
          />
        );
      },
    },
    {
      field: "alphaPattern",
      headerName: "Alpha Pattern",
      width: 250,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        return (
          <CustomSelect
            size="small"
            placeholder="Alpha Pattern"
            value={params.row.alphaPattern}
            onChange={(e) => runUpdate(e, params)}
            menuItemArray={JSON.stringify(patternOptions)}
          />
        );
      },
    },
    {
      field: "betaPattern",
      headerName: "Beta Pattern",
      width: 250,
      headerAlign: "center",
      align: "center",
      editable: true,
      renderCell: (params) => {
        return (
          <CustomSelect
            size="small"
            placeholder="Beta Pattern"
            value={params.row.betaPattern}
            onChange={(e) => runUpdate(e, params)}
            menuItemArray={JSON.stringify(patternOptions)}
          />
        );
      },
    },
  ];

  const PC_Fields = [
    "openPricePC",
    "closePricePC",
    "stopLossPricePC",
    "stopLossPC",
    "profitLossDollarPersonal",
    "feePC",
    "swapPC",
  ];
  const IC_Fields = [
    "openPriceIC",
    "closePriceIC",
    "stopLossPriceIC",
    "stopLossIC",
    "profitLossDollarInvestor",
    "feeIC",
    "swapIC",
  ];

  // Determine which columns to show based on selected account
  const accountId =
    selectedAccountId || localStorage.getItem("selected-account-id") || "All";

  // Determine account type
  let accountType = null;
  if (accountId === "All") {
    accountType = "all";
  } else if (accountId === "All Personal") {
    accountType = "Personal";
  } else if (accountId === "All Investor") {
    accountType = "Investor";
  } else {
    const account = capitalAccounts.find((acc) => acc.id === accountId);
    accountType = account ? account.type : "all";
  }

  // Filter columns based on account type
  if (accountType === "Personal") {
    let filteredData = array.filter((obj) => !IC_Fields.includes(obj.field));
    return filteredData;
  } else if (accountType === "Investor") {
    let filteredData = array.filter((obj) => !PC_Fields.includes(obj.field));
    return filteredData;
  } else {
    return array;
  }
};

export default ColumnsEdit;
